import { RESEND_COOLDOWN_SECONDS } from "components/OtpVerification/cooldown";
import type { ConfirmTrigger } from "pages/StartLead/events";

import type { GrantLookup, GrantMethod } from "./login-grant-flow";

export const GRANT_SEND_KEY = "after.login_grant_send";

type SendStorage = Pick<Storage, "getItem" | "setItem">;

/** The last code or link sent for a grant in this tab. A reload reopens on
 * it instead of sending another; `sessionInfo` is the verification session
 * the texted code is checked against, and `previousSessionInfo` the one of
 * the text before it, whose code verifies too. `count` is how many of that
 * method were sent in a row. */
export type GrantSend =
  | {
      method: "sms_otp";
      sessionInfo: string;
      previousSessionInfo: string | null;
      sentAt: number;
      count: number;
    }
  | { method: "email_link"; sentAt: number; count: number };

/** `replacesEnded` marks a text sent because the code before it ended, so
 * that code's session is not kept to check against. */
export type SentByGrant =
  | { method: "sms_otp"; sessionInfo: string; replacesEnded?: boolean }
  | { method: "email_link" };

export const SEND_REFUSAL_KEY = "after.login_grant_send_refusal";

function sessionStorageOrNull(): SendStorage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function positiveNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

function parseStoredSend(raw: string | null, grant: string): GrantSend | null {
  if (!raw) return null;
  try {
    const value: unknown = JSON.parse(raw);
    if (typeof value !== "object" || value === null) return null;
    const stored = value as Record<string, unknown>;
    if (stored.grant !== grant) return null;
    const { method, sessionInfo, previousSessionInfo, sentAt, count } = stored;
    if (!positiveNumber(sentAt) || !positiveNumber(count)) return null;
    if (method === "email_link") return { method, sentAt, count };
    if (
      method === "sms_otp" &&
      typeof sessionInfo === "string" &&
      sessionInfo !== ""
    ) {
      return {
        method,
        sessionInfo,
        previousSessionInfo:
          typeof previousSessionInfo === "string" && previousSessionInfo !== ""
            ? previousSessionInfo
            : null,
        sentAt,
        count,
      };
    }
    return null;
  } catch {
    return null;
  }
}

/** The send recorded for `grant` in this tab, or null when there is none or
 * the stored one belongs to another grant. */
export function readGrantSend(
  grant: string,
  storage: SendStorage | null = sessionStorageOrNull(),
): GrantSend | null {
  if (!storage) return null;
  try {
    return parseStoredSend(storage.getItem(GRANT_SEND_KEY), grant);
  } catch {
    return null;
  }
}

function previousSessionOf(
  previous: GrantSend | null,
  sessionInfo: string,
): string | null {
  if (previous === null || previous.method !== "sms_otp") return null;
  if (previous.sessionInfo === sessionInfo) return previous.previousSessionInfo;
  return previous.sessionInfo;
}

/** Records a send for `grant`, replacing whatever was stored, and returns
 * it. Only the latest grant is kept. */
export function recordGrantSend(
  grant: string,
  sent: SentByGrant,
  sentAt: number,
  storage: SendStorage | null = sessionStorageOrNull(),
): GrantSend {
  const previous = readGrantSend(grant, storage);
  const count =
    previous !== null && previous.method === sent.method
      ? previous.count + 1
      : 1;
  const send: GrantSend =
    sent.method === "sms_otp"
      ? {
          method: sent.method,
          sessionInfo: sent.sessionInfo,
          previousSessionInfo: sent.replacesEnded
            ? null
            : previousSessionOf(previous, sent.sessionInfo),
          sentAt,
          count,
        }
      : { ...sent, sentAt, count };
  try {
    storage?.setItem(GRANT_SEND_KEY, JSON.stringify({ grant, ...send }));
  } catch {
    // Storage may be full or blocked; a reload then sends a fresh one.
  }
  return send;
}

/** The send a reopened screen resumes on, when the grant still offers its
 * method; null means the screen starts over. */
export function resumableSend(
  send: GrantSend | null,
  lookup: GrantLookup,
): GrantSend | null {
  if (send === null || !lookup.methods.includes(send.method)) return null;
  return send;
}

/** What prompted a send: the screen replacing a code that ended, the
 * screen's own on arrival, the visitor asking for the method the screen
 * already leads with (a resend, or a first send after an automatic one
 * failed), or for the other method. `leading` is the method the screen shows
 * when nothing has been sent. */
export function sendTrigger({
  codeEnded = false,
  automatic,
  method,
  previous,
  leading,
}: {
  codeEnded?: boolean;
  automatic: boolean;
  method: GrantMethod;
  previous: GrantSend | null;
  leading: GrantMethod;
}): ConfirmTrigger {
  if (codeEnded) return "code_ended";
  if (automatic) return "automatic";
  return method === (previous?.method ?? leading) ? "resend" : "switch_method";
}

/** Records that a text for `grant` was refused at `at`, so a reload within
 * the resend cooldown shows the retry screen instead of sending again. Only
 * the latest grant is kept. */
export function recordSendRefusal(
  grant: string,
  at: number,
  storage: SendStorage | null = sessionStorageOrNull(),
): void {
  try {
    storage?.setItem(SEND_REFUSAL_KEY, JSON.stringify({ grant, at }));
  } catch {
    // Storage may be full or blocked; a reload then tries again.
  }
}

/** When a text for `grant` was last refused, if that was within the resend
 * cooldown of `now`; null otherwise. */
export function activeSendRefusal(
  grant: string,
  now: number,
  storage: SendStorage | null = sessionStorageOrNull(),
): number | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(SEND_REFUSAL_KEY);
    if (!raw) return null;
    const value: unknown = JSON.parse(raw);
    if (typeof value !== "object" || value === null) return null;
    const stored = value as Record<string, unknown>;
    if (stored.grant !== grant || !positiveNumber(stored.at)) return null;
    return now - stored.at < RESEND_COOLDOWN_SECONDS * 1000 ? stored.at : null;
  } catch {
    return null;
  }
}
